import {
  DownloadOutlined,
  StepForwardOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { Alert, Button, Row, Spin, Table, Tag, Drawer, List } from "antd";
import {
  HISTORICAL_DATA_TYPE,
  UPLOAD_KIND,
} from "hooks/historicalData/useHistoricalData";
import { useIsMobile } from "hooks/useIsMobile";
import { useCallback, useEffect, useMemo, useState } from "react";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import { useSelfPagination } from "hooks/commons/usePaginationTable";

interface SummaryViewStepProps {
  downloadSummaryFile: (type: HISTORICAL_DATA_TYPE) => void;
  downloadHistoricalLayoutFile: (label: HISTORICAL_DATA_TYPE) => void;
  loading: boolean;
  summaryFile: Blob | undefined;
  setErrorsOnSummary: (anyError: boolean) => void;
  goToMigrateDatabase: () => void;
  kindSelected: UPLOAD_KIND;
}

interface SummaryRowData {
  name: string;
  sener_license: string;
  id_warehouse: string;
  VolInventario: number;
  VolVentas: number;
  ImporteVentas: number;
  TotalVolumenEntradas: number;
  Errores: string;
  kindMismatch?: boolean;
  [key: string]: string | number | boolean | undefined;
}

// Configuración de columnas
const COLUMN_CONFIG = [
  { key: "Errores", title: "Estado", isNumeric: false },
  { key: "name", title: "Nombre", isNumeric: false },
  { key: "sener_license", title: "Número de Permiso", isNumeric: false },
  { key: "id_warehouse", title: "ID Warehouse", isNumeric: false },
  { key: "VolInventario", title: "Vol Inventario", isNumeric: true },
  { key: "VolVentas", title: "Vol Salidas", isNumeric: true },
  { key: "ImporteVentas", title: "Importe Salidas", isNumeric: true },
  {
    key: "TotalVolumenEntradas",
    title: "Total Volumen Entradas",
    isNumeric: true,
  },
] as const;

// Obtener las keys esperadas desde la configuración
const EXPECTED_COLUMNS = COLUMN_CONFIG.map((column) => column.key);

// Definir el tipo para las columnas de la tabla
interface TableColumn {
  title: string;
  dataIndex: string;
  key: string;
  render?: (
    value: string | number,
    record: SummaryRowData,
    index: number
  ) => React.ReactNode;
}

function SummaryViewStep({
  downloadSummaryFile,
  summaryFile,
  loading,
  downloadHistoricalLayoutFile,
  setErrorsOnSummary,
  kindSelected,
  goToMigrateDatabase,
}: SummaryViewStepProps) {
  const [summaryData, setSummaryData] = useState<SummaryRowData[]>([]);
  const [columns, setColumns] = useState<TableColumn[]>([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<SummaryRowData | null>(
    null
  );
  const { isMobile } = useIsMobile();

  // Función para verificar si el número de permiso coincide con el kindSelected
  const checkLicenseKind = useCallback(
    (sener_license: string, kind: UPLOAD_KIND): boolean => {
      if (!sener_license) return false;

      const licenseUpper = sener_license.toUpperCase();

      if (kind === UPLOAD_KIND.PLANTAS) {
        return licenseUpper.includes("/PLA/");
      } else if (kind === UPLOAD_KIND.ESTACIONES) {
        return licenseUpper.includes("/ES/");
      }
      return false;
    },
    []
  );

  // Función para parsear CSV a JSON
  const parseCSV = useCallback(
    (csvText: string): SummaryRowData[] => {
      const lines = csvText.split("\n").filter((line) => line.trim() !== "");
      if (lines.length < 2) return [];

      const headers = lines[0].split(",").map((header) => header.trim());

      return lines.slice(1).map((line) => {
        const values = line.split(",").map((value) => value.trim());
        const row: Partial<SummaryRowData> = {};

        headers.forEach((header, colIndex) => {
          const value = values[colIndex] || "";
          const columnConfig = COLUMN_CONFIG.find((col) => col.key === header);

          // Convertir a número si es una columna numérica
          if (columnConfig?.isNumeric) {
            row[header as keyof SummaryRowData] = value ? Number(value) : 0;
          } else {
            row[header as keyof SummaryRowData] = value;
          }
        });

        // Verificar si hay discrepancia entre el numero de permiso y el kindSelected
        const license = row.sener_license as string;
        row.kindMismatch = license
          ? !checkLicenseKind(license, kindSelected)
          : false;

        return row as SummaryRowData;
      });
    },
    [checkLicenseKind, kindSelected]
  );

  // Verificar si una fila tiene errores
  const hasErrors = useCallback((record: SummaryRowData): boolean => {
    const hasErrorMessages = record.Errores && record.Errores.trim() !== "";
    const hasMissingData = EXPECTED_COLUMNS.some((column) => {
      if (column === "Errores") return false; // Excluir la columna de errores
      const value = record[column];
      return value === "" || value === null || value === undefined;
    });

    return hasErrorMessages || hasMissingData || record.kindMismatch === true;
  }, []);

  // Obtener lista de errores para un registro
  const getErrorList = useCallback(
    (record: SummaryRowData): string[] => {
      const errors: string[] = [];

      // Agregar errores del campo Errores
      if (record.Errores && record.Errores.trim() !== "") {
        errors.push(
          ...record.Errores.split(";")
            .map((error) => error.trim())
            .filter((error) => error)
        );
      }
      return errors;
    },
    [kindSelected]
  );

  // Ordenar datos: primero las filas con errores
  const sortedData = useMemo(() => {
    return [...summaryData].sort((a, b) => {
      const aHasErrors = hasErrors(a);
      const bHasErrors = hasErrors(b);

      if (aHasErrors && !bHasErrors) return -1;
      if (!aHasErrors && bHasErrors) return 1;
      return 0;
    });
  }, [summaryData, hasErrors]);
  const paginationConfig = useSelfPagination(sortedData);

  // Configurar columnas de la tabla
  useEffect(() => {
    if (summaryData.length > 0) {
      const generatedColumns: TableColumn[] = COLUMN_CONFIG.map(
        (columnConfig) => {
          if (columnConfig.key === "Errores") {
            return {
              title: columnConfig.title,
              dataIndex: columnConfig.key,
              key: columnConfig.key,
              render: (_value: string | number, record: SummaryRowData) => {
                if (hasErrors(record)) {
                  return (
                    <Button
                      type="text"
                      icon={
                        <ExclamationCircleOutlined style={{ color: "red" }} />
                      }
                      onClick={() => {
                        setSelectedRecord(record);
                        setDrawerVisible(true);
                      }}
                      title={"Ver errores"}
                    />
                  );
                } else {
                  return (
                    <Button
                      type="text"
                      title="Sin errores"
                      icon={<CheckCircleOutlined style={{ color: "green" }} />}
                    />
                  );
                }
              },
            };
          }

          return {
            title: columnConfig.title,
            dataIndex: columnConfig.key,
            key: columnConfig.key,
            render: (value: string | number, record: SummaryRowData) => {
              // Resaltar si hay discrepancia en la licencia
              if (columnConfig.key === "sener_license" && record.kindMismatch) {
                return (
                  <span style={{ color: "red", fontWeight: "bold" }}>
                    {value}
                  </span>
                );
              }

              if (columnConfig.isNumeric && typeof value === "number") {
                return value.toLocaleString();
              }
              return value || <Tag color="red">Sin valor</Tag>;
            },
          };
        }
      );

      setColumns(generatedColumns);
    }
  }, [summaryData, hasErrors]);

  // Procesar el archivo cuando esté disponible
  useEffect(() => {
    if (summaryFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const parsedData = parseCSV(text);
        setSummaryData(parsedData);
      };
      reader.readAsText(summaryFile);
    }
  }, [summaryFile, parseCSV]);

  // Descargar el archivo al montar el componente
  useEffect(() => {
    downloadSummaryFile(HISTORICAL_DATA_TYPE.PRE_VALIDATION_SUMMARY);
  }, [downloadSummaryFile]);

  const recordsWithErrorsCount: number = useMemo(() => {
    return summaryData.filter(hasErrors).length;
  }, [summaryData, hasErrors]);

  const kindMismatchCount: number = useMemo(() => {
    return summaryData.filter((record) => record.kindMismatch).length;
  }, [summaryData]);

  useEffect(() => {
    setErrorsOnSummary(recordsWithErrorsCount !== 0);
  }, [recordsWithErrorsCount, setErrorsOnSummary]);

  return (
    <div style={{ paddingTop: "4rem" }}>
      <Spin spinning={loading}>
        {summaryData.length > 0 ? (
          <>
            <Row gutter={24}>
              <ResponsiveCol size="xl">
                {recordsWithErrorsCount > 0 && (
                  <Alert
                    message={`Existen ${recordsWithErrorsCount} registros con problemas`}
                    description={
                      <>
                        <div>
                          - {recordsWithErrorsCount} números de permiso que no
                          se presentaron en todos los layouts
                        </div>
                        {kindMismatchCount > 0 && (
                          <div>
                            - {kindMismatchCount} registros con tipo de licencia
                            que no coincide con {kindSelected}
                          </div>
                        )}
                        <div>
                          Haz clic en los iconos de advertencia para ver los
                          detalles de cada error
                        </div>
                      </>
                    }
                    type="error"
                    showIcon
                  />
                )}
              </ResponsiveCol>
            </Row>

            <Row style={{ paddingTop: "1rem" }}>
              <ResponsiveCol size="xl">
                <Button
                  variant="outlined"
                  color="blue"
                  icon={<DownloadOutlined />}
                  style={{ float: "right" }}
                  onClick={() =>
                    downloadHistoricalLayoutFile(
                      HISTORICAL_DATA_TYPE.PRE_VALIDATION_SUMMARY
                    )
                  }
                >
                  Descargar Resumen de prevalidación
                </Button>
              </ResponsiveCol>
            </Row>

            <Table<SummaryRowData>
              dataSource={sortedData}
              columns={columns}
              pagination={paginationConfig}
              scroll={{ x: true }}
              size="middle"
              rowClassName={(record) =>
                hasErrors(record) ? "row-with-inconsistencies" : ""
              }
            />

            {/* Drawer para mostrar errores */}
            <Drawer
              title="Detalles de errores"
              placement="bottom"
              onClose={() => setDrawerVisible(false)}
              open={drawerVisible}
            >
              {selectedRecord && (
                <div>
                  <h3>Punto de Distribución: {selectedRecord.name}</h3>
                  <p>
                    <strong>Número de Permiso:</strong>{" "}
                    {selectedRecord.sener_license}
                  </p>
                  <h4>Errores encontrados:</h4>
                  <List
                    size="small"
                    bordered
                    dataSource={getErrorList(selectedRecord)}
                    renderItem={(error) => (
                      <List.Item>
                        <ExclamationCircleOutlined
                          style={{ color: "red", marginRight: 8 }}
                        />
                        {error}
                      </List.Item>
                    )}
                  />
                </div>
              )}
            </Drawer>
          </>
        ) : (
          <Alert
            message="Sin datos"
            description="No se encontró información de resumen para el período seleccionado."
            type="warning"
            showIcon
          />
        )}
      </Spin>

      <Row>
        {recordsWithErrorsCount === 0 && (
          <ResponsiveCol size="xl">
            <Alert
              message="¿Estás seguro de que deseas validar esta información?"
              description={`Ten en cuenta que el proceso de carga historica eliminará los datos operativos
                sobre el periodo elegido, luego de lo cual ingresará los datos desde los layouts que cargaste.
                A partir del siguiente paso, toda acción afectará a la base de datos productiva
                y no podrá revertirse. Continua con precaución.
                `}
              type="warning"
              showIcon
            />
          </ResponsiveCol>
        )}
        <ResponsiveCol size="xl">
          <Button
            type="primary"
            style={{
              float: "right",
              marginTop: "10px",
              whiteSpace: "pre-wrap",
              height: isMobile ? "unset" : undefined,
            }}
            icon={<StepForwardOutlined />}
            disabled={recordsWithErrorsCount !== 0}
            onClick={goToMigrateDatabase}
          >
            Aceptar prevalidación y continuar para migrar la base de datos
          </Button>
        </ResponsiveCol>
      </Row>
    </div>
  );
}

export default SummaryViewStep;
